
/* ============================== src/hooks/useEntityFiles.js ============================== */
// src/hooks/useEntityFiles.js
import { useCallback, useMemo, useState } from "react";
import {
  useMoveFileMutation,
  useMoveFolderMutation,
  useRenameFileMutation,
} from "../api/queries/files";
import { useTaskFileActions } from "./useTaskFileActions";

/**
 * Хук для управления файлами и папками в карточках задач и проектов.
 * Синхронизирует локальный стейт при изменении сущности и предоставляет
 * оптимизированные коллбэки для перемещения и переименования.
 */
export function useEntityFiles({ existing, ownerType, toast, confirm }) {
  const [localFiles, setLocalFiles] = useState(() => existing?.files || []);
  const [localFolders, setLocalFolders] = useState(
    () => existing?.folders || [],
  );
  const [prevExisting, setPrevExisting] = useState(existing);

  // Синхронизация локального стейта при смене сущности (без useEffect)
  if (existing !== prevExisting) {
    setPrevExisting(existing);
    setLocalFiles(existing?.files || []);
    setLocalFolders(existing?.folders || []);
  }

  const setFileFieldValue = useCallback((field, value) => {
    if (field === "files") setLocalFiles(value);
    else if (field === "folders") setLocalFolders(value);
  }, []);

  // Деструктурируем mutate для стабильных зависимостей в useCallback
  const { mutate: mutateMoveFile } = useMoveFileMutation();
  const { mutate: mutateMoveFolder } = useMoveFolderMutation();
  const { mutate: mutateRenameFile } = useRenameFileMutation();

  const handleMoveFile = useCallback(
    (file, targetFolderId) => {
      mutateMoveFile(
        { fileId: file.id, folderId: targetFolderId },
        {
          onSuccess: () => {
            toast("Файл перемещён", "success");
            setLocalFiles((prev) =>
              prev.map((f) =>
                f.name === file.name && f.folderId === file.folderId
                  ? { ...f, folderId: targetFolderId }
                  : f,
              ),
            );
          },
          onError: (err) =>
            toast(err.message || "Ошибка перемещения файла", "error"),
        },
      );
    },
    [mutateMoveFile, toast],
  );

  const handleMoveFolder = useCallback(
    (folder, parentId) => {
      mutateMoveFolder(
        { folderId: folder.id, parentId },
        {
          onSuccess: () => {
            toast("Папка перемещена", "success");
            setLocalFolders((prev) =>
              prev.map((f) => (f.id === folder.id ? { ...f, parentId } : f)),
            );
          },
          onError: (err) =>
            toast(err.message || "Ошибка перемещения папки", "error"),
        },
      );
    },
    [mutateMoveFolder, toast],
  );

  const handleRenameFile = useCallback(
    (file, newName) => {
      mutateRenameFile(
        { fileId: file.id, name: newName },
        {
          onSuccess: () => {
            toast("Файл переименован", "success");
            setLocalFiles((prev) =>
              prev.map((f) => (f.id === file.id ? { ...f, name: newName } : f)),
            );
          },
          onError: (err) =>
            toast(err.message || "Ошибка переименования", "error"),
        },
      );
    },
    [mutateRenameFile, toast],
  );

  // Минимальный объект для совместимости с useTaskFileActions
  const valuesProxy = useMemo(
    () => ({
      id: existing?.id,
      files: localFiles,
      folders: localFolders,
    }),
    [existing?.id, localFiles, localFolders],
  );

  const {
    handleFileUpload,
    handleFileDelete,
    handleCreateFolder,
    handleDeleteFolder,
  } = useTaskFileActions({
    values: valuesProxy,
    existing,
    setFieldValue: setFileFieldValue,
    toast,
    confirm,
    ownerType,
  });

  return {
    localFiles,
    localFolders,
    handleMoveFile,
    handleMoveFolder,
    handleRenameFile,
    handleFileUpload,
    handleFileDelete,
    handleCreateFolder,
    handleDeleteFolder,
  };
}
